import React from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { motion } from 'framer-motion';
import { Users, MessageSquare, Star, Briefcase, Trophy, BookOpen, Heart, UserCheck, Globe } from 'lucide-react';

const platforms = [
    {
        name: 'WhatsApp Community',
        members: '300+',
        description: 'Quick discussions, daily tips, and instant peer support. Perfect for staying connected on the go.',
        features: ['Daily design tips', 'Quick Q&A', 'Job alerts', 'Event reminders'],
        link: 'https://chat.whatsapp.com/your-community',
        icon: MessageSquare,
    },
    {
        name: 'Discord Server',
        members: '250+',
        description: 'In-depth conversations, project showcases, voice sessions, and dedicated channels for every topic.',
        features: ['Project showcases', 'Voice hangouts', 'Resource channels', 'Mentor sessions'],
        link: 'https://discord.gg/your-server',
        icon: Globe,
    },
];

const communityFeatures = [
    { icon: Star, title: 'Peer Reviews', description: 'Get constructive feedback on your projects from fellow students and mentors.' },
    { icon: Briefcase, title: 'Job Referrals', description: 'Access exclusive job openings shared by alumni and industry partners.' },
    { icon: Trophy, title: 'Weekly Challenges', description: 'Participate in design and code challenges to sharpen your skills and win prizes.' },
    { icon: BookOpen, title: 'Resource Library', description: 'Curated collection of templates, kits, articles, and learning materials.' },
    { icon: UserCheck, title: 'Mentor Office Hours', description: 'Weekly open sessions where you can ask mentors anything,一对一.' },
    { icon: Heart, title: 'Alumni Network', description: 'Stay connected with graduates who are now working at top companies.' },
];

const stats = [
    { value: '500+', label: 'Active Members' },
    { value: '50+', label: 'Projects Shared Weekly' },
    { value: '20+', label: 'Job Referrals This Month' },
];

const testimonials = [
    {
        name: 'Sneha R.',
        role: 'UI/UX Student, Batch 2026',
        text: 'The community is the best part of the course. I got my first freelance client through a referral here!',
    },
    {
        name: 'Vikram P.',
        role: 'Frontend Developer',
        text: 'The weekly challenges pushed me to learn more than I would on my own. The peer reviews are incredibly helpful.',
    },
    {
        name: 'Anita K.',
        role: 'Graphic Designer',
        text: 'I landed my current job because an alumni referred me. The network here is genuinely supportive.',
    },
];

const Community = () => {
    return (
        <>
            <Helmet>
                <title>Community | PyrusMedia</title>
                <meta name="description" content="Join 500+ students and professionals in the PyrusMedia learning community. Network, share projects, and grow together." />
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#bebd19]/20 rounded-full blur-[100px] pointer-events-none" />
                <div className="container mx-auto px-4 relative z-10">
                    <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">Join Our Community</h1>
                    <p className="text-xl text-gray-500 max-w-3xl mx-auto">
                        Network, share projects, get feedback, and access exclusive resources.
                    </p>
                </div>
            </section>

            {/* Platform Cards */}
            <Section className="bg-gray-50 py-20">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Platform</h2>
                    <p className="text-gray-500">Join the space that fits your learning style.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {platforms.map((platform, index) => {
                        const Icon = platform.icon;
                        return (
                            <motion.div
                                key={platform.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.15 }}
                                className="bg-white rounded-2xl border border-gray-200 p-8 hover:border-[#bebd19] transition-all duration-300 flex flex-col"
                            >
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-14 h-14 rounded-xl bg-[#bebd19]/10 flex items-center justify-center">
                                        <Icon className="text-[#bebd19] w-7 h-7" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">{platform.name}</h3>
                                        <span className="text-sm text-[#bebd19] font-semibold">{platform.members} members</span>
                                    </div>
                                </div>
                                <p className="text-gray-500 mb-6 flex-grow">{platform.description}</p>
                                <ul className="space-y-2 mb-8">
                                    {platform.features.map((feature) => (
                                        <li key={feature} className="flex items-center gap-2 text-sm text-gray-500">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#bebd19]" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href={platform.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-primary w-full text-center"
                                >
                                    Join {platform.name}
                                </a>
                            </motion.div>
                        );
                    })}
                </div>
            </Section>

            {/* Community Features */}
            <Section className="bg-white py-20">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">What You Get</h2>
                    <p className="text-gray-500">More than just a chat group \u2014 a complete learning ecosystem.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {communityFeatures.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.3, delay: index * 0.08 }}
                                className="bg-gray-100 p-6 rounded-2xl border border-gray-200 hover:border-[#bebd19] transition-colors"
                            >
                                <Icon className="text-[#bebd19] w-8 h-8 mb-4" />
                                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </Section>

            {/* Stats */}
            <Section className="bg-[#bebd19] py-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                    {stats.map((stat) => (
                        <div key={stat.label}>
                            <div className="text-5xl font-bold text-gray-900 mb-2">{stat.value}</div>
                            <div className="text-gray-900/70 font-medium">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Testimonials */}
            <Section className="bg-gray-50 py-20">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">What Members Say</h2>
                    <p className="text-gray-500">Hear from students who have grown with our community.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {testimonials.map((t, index) => (
                        <motion.div
                            key={t.name}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className="bg-white p-8 rounded-2xl border border-gray-200"
                        >
                            <p className="text-gray-500 mb-6 leading-relaxed italic">"{t.text}"</p>
                            <div>
                                <p className="font-bold text-gray-900">{t.name}</p>
                                <p className="text-sm text-[#bebd19]">{t.role}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* CTA */}
            <Section className="bg-white py-20">
                <div className="text-center max-w-2xl mx-auto">
                    <h2 className="text-4xl font-bold text-gray-900 mb-6">Join the Community \u2014 It&apos;s Free</h2>
                    <p className="text-gray-500 text-lg mb-8">
                        Connect with 500+ like-minded designers and developers. Start learning, sharing, and growing today.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button to="https://chat.whatsapp.com/your-community">
                            <Users size={18} className="mr-2" /> Join WhatsApp
                        </Button>
                        <Button to="https://discord.gg/your-server" variant="outline">
                            <Globe size={18} className="mr-2" /> Join Discord
                        </Button>
                    </div>
                </div>
            </Section>
        </>
    );
};

export default Community;
